<template>
    <v-container fluid>
        <v-row justify="center">
            <v-col cols="12">
                <responsiveButton :setText="true"
                                  setColor="orange"
                                  setDefaultIcon="mdi-refresh"
                                  setSmallIcon="mdi-refresh"
                                  :setClick="initialize"
                >
                    Refresh Data
                </responsiveButton>
                <v-divider class="mx-6"></v-divider>
                <v-data-table
                        :headers="employeesTable.headers"
                        :items="employees"
                        class="elevation-1"
                        :options.sync="pagination"
                        :server-items-length="totalItems"
                        :loading="loading"
                        loading-text="Loading... Please wait"
                >
                    <template v-slot:item.emailVerified="{item}">
                        <v-chip :color="getColorForEmail(item.emailVerified)" dark>
                            {{item.emailVerified?'': 'Not '}} Verified
                        </v-chip>
                    </template>
                    <!--actions-->
                    <template v-slot:item.view="{item}">
                        <v-icon
                                class="mr-2"
                                @click="viewEmployeeDetails(item)"
                        >
                            {{ EMPLOYEE_ACTIONS.VIEW.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.profile="{item}">
                        <v-icon
                                class="mr-2"
                                @click="editEmployee(item)"
                        >
                            {{ EMPLOYEE_ACTIONS.EDIT.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.view="{item}">
                        <v-icon
                                class="mr-2"
                                @click="viewEmployeeDetails(item)"
                        >
                            mdi-eye
                        </v-icon>
                    </template>
                    <template v-slot:item.documents="{item}">
                        <v-icon
                                class="mr-2"
                                @click="editEmployeeFiles(item)"
                        >
                            {{ EMPLOYEE_ACTIONS.EDIT_DOCUMENTS.icon }}
                        </v-icon>
                    </template>
                    <template v-slot:item.remove="{item}">
                        <v-icon
                                class="mx-auto"
                                @click="confirmDelete(item)"
                        >
                            {{ EMPLOYEE_ACTIONS.DELETE.icon }}
                        </v-icon>
                    </template>
                </v-data-table>
            </v-col>
        </v-row>
        <dialog-alert
                :text="confirmAlertText"
                :alert-type="confirmAlertType"
                :open="openConfirmation"
                @alert-closed="dialogClosed($event)"
        ></dialog-alert>
    </v-container>
</template>

<script>
    import alertEventBus from '@/eventBus/AlertEventBus';
    const responsiveButton = ()=> import( '@/components/common/mobile/Button');
    const DialogAlert = () => import("@/components/common/DialogAlert");
    import { ALERT_TYPES, CONFIRM_DELETE } from "@/shared/enums/AlertTexts";
    import {EMPLOYEE_ACTIONS} from "@/shared/enums/Employee";

    export default {
        name: "employeeTable",
        components: { responsiveButton, DialogAlert },
        data(){
            return {
                employees: [],
                loading: true,
                employeesTable: {
                    headers: [
                        {
                            text: 'Name',
                            align: 'center',
                            sortable: true,
                            value: 'name',
                        },
                        {
                            text: 'Username',
                            align: 'center',
                            sortable: true,
                            value: 'cognitoUsername'
                        },
                        {
                            text: 'Employee Type',
                            align: 'center',
                            sortable: true,
                            value: 'typeName'
                        },
                        {
                            text: 'Verified',
                            align: 'center',
                            sortable: true,
                            divider: true,
                            value: 'emailVerified'
                        },
                        {
                            text: 'View',
                            align: 'center',
                            width: '2%',
                            value: 'view',
                            sortable: false
                        },
                        {
                            text: 'Profile',
                            align: 'center',
                            width: '2%',
                            value: 'profile',
                            sortable: false
                        },
                        {
                            text: 'Edit Category',
                            align: 'center',
                            width: '8%',
                            value: 'documents',
                            sortable: false
                        },
                        {
                            text: 'Remove',
                            align: 'center',
                            width: '2%',
                            value: 'remove',
                            sortable: false
                        }
                    ]
                },
                confirmAlertType: ALERT_TYPES.CONFIRM,
                confirmAlertText: CONFIRM_DELETE,
                openConfirmation: false,
                employeeToDelete: null,
                pagination: {},
                pageKeyCache: new Map(),
                totalItems: 0
            };
        },
        computed:{
            EMPLOYEE_ACTIONS(){
                return EMPLOYEE_ACTIONS;
            }
        },
        watch: {
            pagination: {
                async handler() {
                    await this.getEmployees();
                },
                deep: true
            },
        },
        methods:{
            initialize() {
                this.getEmployees();
            },
            getColorForEmail(isVerified){
              if(isVerified){
                  return 'green';
              }
              return 'red';
            },
            async getEmployees() {
                this.loading = true;
                let params = this.getPaginationValues();

                if(!params.limit)
                    return;

                try{
                    await this.$store.dispatch('getEmployees', params);
                    this.employees = this.$store.getters.getEmployees;
                }
                catch (e) {
                    //set alert
                    this.loading = false;
                    alertEventBus.setAlert({type:'error', msg: e.message});
                    return;
                }

                this.setPaginationValues(this.employees);

                this.loading = false;
            },
            setPaginationValues(list){
                let key = this.$store.getters.getEmployeesPageKey;
                const {page} = this.pagination;

                if(key != null && Object.keys(key).length > 0){
                    this.pageKeyCache.set(page, key);
                    this.totalItems = list.length +1;
                }
                else{
                    this.totalItems = list.length;
                }
            },
            getPaginationValues(){
                //all values of pagination that can be used for backend
                const { page, itemsPerPage } = this.pagination;
                let params = {
                    limit: itemsPerPage
                }

                if(this.pageKeyCache.has(page) && page != 1){
                    params.lastEvaluatedKey = this.pageKeyCache.get(page);
                }
                else {
                    //we have not been there yet, so it needs to be to cases 1: this is brand new search page 0
                    // or it is the next page that we has not seem
                    if(page != 1) {
                        params.lastEvaluatedKey = this.pageKeyCache.get(page-1);
                    }
                }
                return params;
            },
            async editEmployee(employee){
                this.$store.commit('setEmployee',employee);
                this.$router.push({name: EMPLOYEE_ACTIONS.EDIT.route, params:{id: employee.id}});
            },
            async editEmployeeFiles(employee){
                const index = this.employees.indexOf(employee);
                this.$store.commit('setEmployee',employee);
                this.$router.push({name: EMPLOYEE_ACTIONS.EDIT_DOCUMENTS.route, params:{id: employee.id}});
            },
            async deleteEmployee(employee) {
                const index = this.employees.indexOf(employee);

                try {
                    await this.$store.dispatch("deleteEmployee", {
                        id: employee.id
                    });
                    await this.$store.dispatch("deleteCognitoUser", {
                        username: employee.cognitoUsername
                    });
                } catch (e) {
                    alertEventBus.setAlert({ type: "error", msg: e.message });
                    return;
                }

                this.employees.splice(index, 1);
                this.deleteEmployee = null;
            },
            viewEmployeeDetails(employee){
                this.$store.commit('setEmployee',{...employee});
                this.$router.push({name: EMPLOYEE_ACTIONS.VIEW.route, params:{id:employee.id}});
            },
            confirmDelete(item) {
                this.openConfirmation = true;
                this.employeeToDelete = item;
            },
            dialogClosed(answer) {
                this.openConfirmation = false;
                if (answer) {
                    this.deleteEmployee(this.employeeToDelete);
                }
            }
        },
        async created(){
            this.initialize();
        }
    }
</script>

<style>
    .v-chip.white-span span {
        color: white !important;
    }
</style>